import { useState } from "react";
import { format, parseISO } from "date-fns";
import { ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { TechLink } from "@/components/links";
import { resolveWindow } from "@/lib/format";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { WindowBadge } from "@/components/ui/WindowBadge";
import { useCustomerDispatches, type CustomerDispatchRow } from "./api";
import { DispatchPeek } from "./DispatchPeek";
import { useVisitHeadlines, type VisitHeadline } from "./visitSummary";
import { Th, useSort } from "@/features/reports/lists/listCore";

interface Props {
  custno: string;
  locno?: string | null;
}

export function CustomerDispatches({ custno, locno }: Props) {
  const { data, isLoading } = useCustomerDispatches(custno);
  // SERVICE-ONELINE-01: the What column reads the same one-line summary as
  // the Overview and History — AI when written, rule-based / cleaned meanwhile.
  const { headlineFor } = useVisitHeadlines(custno, data ?? []);
  // FLOW-01: rows peek in place — the dispatcher stays on the customer page.
  const [peekDispatch, setPeekDispatch] = useState<string | null>(null);
  // Sortable headers — same machinery as the Reports lists.
  const sort = useSort<CustomerDispatchRow>("date", "desc");
  const SORTERS: Record<string, (r: CustomerDispatchRow) => string | number | null | undefined> = {
    date: (r) => r.appointment_date,
    window: (r) => resolveWindow(r.window_code, r.notes).value,
    tech: (r) => r.tech_name ?? `${r.tech_first ?? ""} ${r.tech_last ?? ""}`.trim(),
    status: (r) => r.status ?? "Pending",
    dispatch: (r) => Number(r.dispatch) || r.dispatch,
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="h-12 animate-pulse bg-slate-100" />
        ))}
      </div>
    );
  }

  // If a location is selected, optionally filter to just that location.
  // For now show all - many customers want to see all their history.
  const rows = (data ?? []).filter((r) =>
    locno ? r.locno === locno : true,
  );

  if (rows.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-slate-500">
        No dispatch history for this {locno ? "location" : "customer"}.
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold">
            <Th id="date" sort={sort}>Date</Th>
            <Th id="window" sort={sort}>Window</Th>
            <Th id="tech" sort={sort}>Tech</Th>
            <th className="px-3 py-2 uppercase tracking-wider text-slate-500">What</th>
            <Th id="status" sort={sort}>Status</Th>
            <Th id="dispatch" sort={sort} align="right">Dispatch</Th>
          </tr>
        </thead>
        <tbody>
          {sort.sortRows(rows, SORTERS).map((r, i) => (
            <DispatchHistoryRow
              // Index disambiguates multi-visit rows (same dispatch+tech) —
              // duplicate keys freeze React row reordering (the sort bug).
              key={`${r.dispatch}-${r.tech_empno}-${i}`}
              row={r}
              headline={headlineFor(r)}
              onPeek={setPeekDispatch}
            />
          ))}
        </tbody>
      </table>
      <DispatchPeek
        dispatchId={peekDispatch}
        onClose={() => setPeekDispatch(null)}
      />
      {rows.length === 100 && (
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 text-center text-xs text-slate-500">
          Showing the most recent 100. Older history available — we&apos;ll add
          pagination soon.
        </div>
      )}
    </Card>
  );
}

function DispatchHistoryRow({
  row,
  headline,
  onPeek,
}: {
  row: CustomerDispatchRow;
  headline: VisitHeadline;
  onPeek: (dispatch: string) => void;
}) {
  const win = resolveWindow(row.window_code, row.notes);
  const what = headline.text;
  const techName =
    row.tech_name ??
    `${row.tech_first ?? ""} ${row.tech_last ?? ""}`.trim();
  let dateLabel = "—";
  try {
    dateLabel = format(parseISO(row.appointment_date), "MMM d, yyyy");
  } catch {
    dateLabel = row.appointment_date;
  }

  // FLOW-01 (Seth): the whole row PEEKS the dispatch in place — the
  // dispatcher stays on the customer page; "Open full dispatch" inside the
  // peek is the deliberate drill-down. The Tech cell stops propagation so it
  // still drills to the tech.
  function openDispatch() {
    onPeek(row.dispatch);
  }

  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={openDispatch}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDispatch();
        }
      }}
      className="group cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
    >
      <td className="px-3 py-2 align-top whitespace-nowrap text-slate-700">
        {dateLabel}
      </td>
      <td className="px-3 py-2 align-top">
        {win.value ? (
          <WindowBadge code={win.value} />
        ) : (
          <span className="text-xs text-slate-500">—</span>
        )}
      </td>
      <td
        className="px-3 py-2 align-top"
        onClick={(e) => e.stopPropagation()}
      >
        <TechLink empno={row.tech_empno} name={techName || "—"} />
      </td>
      <td className="px-3 py-2 align-top text-xs text-slate-700" title={headline.raw || undefined}>
        <span className="font-medium">{what}</span>
        {headline.amount != null && (
          <span className="ml-2 font-mono text-[11px] tabular-nums text-slate-400">
            {headline.amount.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <StatusBadge status={row.status} variant="pill" />
      </td>
      <td className="px-3 py-2 text-right align-top">
        <span className="inline-flex items-center gap-1 font-mono text-xs text-slate-500 group-hover:text-brand-700">
          #{row.dispatch}
          <ChevronRight className="h-3.5 w-3.5 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-500" />
        </span>
      </td>
    </tr>
  );
}
